import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { MenuItem, Select, Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { APP_ICONS } from "../../../assets";
import { deleteConfirmAlert } from "../../../components/Alert";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import NavigationDrawer from "../../../components/NavigationDrawer";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import {
  closeCampaign,
  deleteCampaign,
  getAllCampaigns,
  publishCampaign,
} from "../../../services/campaignApiFn";
import { handleOpenSnackBar } from "../../../utils";
import AddEditCampaign from "./AddEditCampaign";
import CampaignParticipants from "./CampaignParticipants";

const CAMPAIGN_STATUS = ["draft", "published", "closed"];

const headerData = [
  "createdAt",
  "campaignDetails",
  "campaignType",
  "kpis",
  "commission",
  "maxCreators",
  "campaignStatus",
  "actions",
];

const tableRows = [
  "created_date",
  "campaign_details",
  "campaign_type",
  "campaign_kpis",
  "campaign_commission",
  "campaign_max_creators",
  "campaign_status",
  "campaign_actions",
];

function CampaignPage() {
  const campaignDialogRef = useRef(null);
  const participantsDialogRef = useRef(null);
  const fetchIdRef = useRef(0);
  const { t } = useTranslation(["campaign", "common"]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const debouncedSearch = useDebounce(search, 1000);

  const fetchAllCampaigns = async (
    page = pagination.page,
    limit = pagination.limit,
  ) => {
    const fetchId = ++fetchIdRef.current;
    try {
      setIsLoading(true);
      const response = await getAllCampaigns({
        page,
        limit,
        search: debouncedSearch,
        status,
      });
      if (fetchId !== fetchIdRef.current) return;
      if (response?.data?.status === "success") {
        const paginationData = response?.data?.data?.pagination || {};
        setAllCampaigns(response?.data?.data?.data || []);
        setPagination({
          page: paginationData.page || page,
          limit: paginationData.limit || limit,
          total: paginationData.total || 0,
        });
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      handleOpenSnackBar("error", error?.message);
    } finally {
      if (fetchId === fetchIdRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCampaigns(1, pagination.limit);
  }, [debouncedSearch, status]);

  const handlePageChange = (_, newPage) => {
    fetchAllCampaigns(newPage + 1, pagination.limit);
  };

  const handleRowsPerPageChange = (event) => {
    fetchAllCampaigns(1, parseInt(event.target.value, 10));
  };

  const handleOpenAddDialog = () => {
    campaignDialogRef.current.openDialog({
      type: "add",
      getData: () => fetchAllCampaigns(1, pagination.limit),
    });
  };

  const handleOpenEditDialog = ({ row }) => {
    campaignDialogRef.current.openDialog({
      type: "edit",
      data: row,
      getData: () => fetchAllCampaigns(),
    });
  };

  // Delete / Publish / Close sab ek hi flow se chalte hain
  const handleConfirmAction = async ({ apiFunction, id, title, text }) => {
    const confirmed = await deleteConfirmAlert({
      title,
      text,
      confirmButtonText: t("confirm"),
      cancelButtonText: t("cancel", { ns: "common" }),
    });
    if (!confirmed) return;

    try {
      setIsLoading(true);
      const response = await apiFunction(id);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        fetchAllCampaigns();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = ({ row }) =>
    handleConfirmAction({
      apiFunction: deleteCampaign,
      id: row?._id,
      title: t("confirmation", { ns: "common" }),
      text: t("deleteCampaignConfirmation"),
    });

  const handlePublish = (row) =>
    handleConfirmAction({
      apiFunction: publishCampaign,
      id: row?._id,
      title: t("publishCampaign"),
      text: t("publishCampaignConfirmation"),
    });

  const handleClose = (row) =>
    handleConfirmAction({
      apiFunction: closeCampaign,
      id: row?._id,
      title: t("closeCampaign"),
      text: t("closeCampaignConfirmation"),
    });

  const renderHeaderAction = () => {
    return (
      <Stack
        direction={"row"}
        gap={2}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"contained"}
          btnText={"addNew"}
          styleProps={{ height: 40 }}
          handleClickBtn={handleOpenAddDialog}
        />
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack
        p={2}
        gap={2}
        direction={{ md: "row" }}
        bgcolor={"primary.light"}
      >
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} alt="" />}
          InputEndIcon={
            search && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => setSearch("")}
              />
            )
          }
          placeholder={t("searchByCampaignName")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          size="small"
          displayEmpty
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{
            width: { xs: "100%", md: 200 },
            backgroundColor: "#fff",
          }}
        >
          <MenuItem value="">{t("allStatus")}</MenuItem>
          {CAMPAIGN_STATUS.map((item) => (
            <MenuItem key={item} value={item}>
              {t(item, { ns: "common" })}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    );
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("allCampaigns")}
        subtitle={t("campaignDescription")}
        headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        <DynamicPaginatedTable
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allCampaigns}
          handleEdit={handleOpenEditDialog}
          handleDelete={handleDelete}
          handlePublishCampaign={handlePublish}
          handleCloseCampaign={handleClose}
          handleViewParticipants={(row, tab) =>
            participantsDialogRef.current.openDialog({ data: row, tab })
          }
          paginatedData={pagination}
          showDynamicPagination={true}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 30, 50, 100]}
        />
        <AddEditCampaign ref={campaignDialogRef} />
        <CampaignParticipants
          ref={participantsDialogRef}
          onUpdated={() => fetchAllCampaigns()}
        />
      </PaperContainer>
    </NavigationDrawer>
  );
}

export default CampaignPage;
